import { useState } from "react";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useCatalog } from "@/hooks/useCatalog";
import { api, errMsg, gbp } from "@/lib/api";

export const CartDrawer = () => {
  const { items, setQty, open, setOpen } = useCart();
  const { products } = useCatalog();
  const [busy, setBusy] = useState(false);
  const rows = items
    .map((i) => ({ ...i, p: products.find((p) => p.lookup_key === i.lookup_key) }))
    .filter((r) => r.p);
  const total = rows.reduce((s, r) => s + r.p.amount * r.quantity, 0);

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout/shop", {
        items: rows.map((r) => ({ lookup_key: r.lookup_key, quantity: r.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col bg-[#FAF8F5] sm:max-w-md" data-lenis-prevent data-testid="cart-drawer">
        <SheetHeader>
          <p className="eyebrow text-[#B83560]">Flapjack</p>
          <SheetTitle className="font-display text-3xl">Your basket</SheetTitle>
          <SheetDescription>UK delivery. Shipping details collected at checkout.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 flex-1 space-y-4 overflow-y-auto">
          {rows.length === 0 && (
            <div className="grid place-items-center py-20 text-center text-stone-600" data-testid="cart-empty">
              <ShoppingBag className="mb-4 h-8 w-8" />
              Your basket is empty.
            </div>
          )}
          {rows.map((r) => (
            <div key={r.lookup_key} className="flex gap-4 rounded-2xl bg-white p-3" data-testid={`cart-item-${r.lookup_key}`}>
              <img src={r.p.image} alt={r.p.name} className="h-20 w-20 rounded-xl bg-[#FFD8E2] object-contain p-1" />
              <div className="flex flex-1 flex-col">
                <p className="font-semibold leading-tight">{r.p.name}</p>
                <p className="text-xs text-stone-600">A recipe by Chef Will</p>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(r.lookup_key, r.quantity - 1)} className="grid h-7 w-7 place-items-center rounded-full border" data-testid={`cart-decrease-${r.lookup_key}`} aria-label="Decrease">
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="w-6 text-center text-sm font-semibold" data-testid={`cart-qty-${r.lookup_key}`}>{r.quantity}</span>
                    <button onClick={() => setQty(r.lookup_key, r.quantity + 1)} className="grid h-7 w-7 place-items-center rounded-full border" data-testid={`cart-increase-${r.lookup_key}`} aria-label="Increase">
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <span className="font-semibold">{gbp(r.p.amount * r.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t pt-5">
          <div className="mb-4 flex justify-between text-lg">
            <span>Subtotal</span>
            <span className="font-display font-bold" data-testid="cart-subtotal">{gbp(total)}</span>
          </div>
          <button disabled={!rows.length || busy} onClick={checkout} className="btn-ink w-full justify-center" data-testid="cart-checkout-button">
            {busy ? "Opening secure checkout…" : "Checkout securely"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
